/* Each card upgrades when inserted, including Shopify's AJAX collection renders. */
(() => {
  'use strict';
  if (!window.customElements || customElements.get('vv-sock-card')) return;

  class VVSockCard extends HTMLElement {
    connectedCallback() {
      // Also supports a custom element inserted before its child markup is parsed.
      queueMicrotask(() => {
        if (!this.isConnected || this.ready) return;
        this.track = this.querySelector('[data-vv-sock-track]');
        this.controls = this.querySelector('[data-vv-sock-controls]');
        this.slides = Array.from(this.querySelectorAll('[data-vv-sock-slide]'));
        if (!this.track || !this.controls || this.slides.length < 2) return;

        this.ready = true;
        this.index = 0;
        this.requestedIndex = null;
        this.manualSelection = this.manualSelection || false;
        this.hoverPreview = false;
        this.stage = this.track.closest('.vv-sock-card__stage') || this.track;
        this.dots = Array.from(this.querySelectorAll('[data-vv-sock-index]'));
        this.previous = this.querySelector('[data-vv-sock-step="-1"]');
        this.next = this.querySelector('[data-vv-sock-step="1"]');
        this.status = this.querySelector('[data-vv-sock-status]');
        this.onClick = this.handleClick.bind(this);
        this.onKeyDown = this.handleKeyDown.bind(this);
        this.onScroll = this.handleScroll.bind(this);
        this.onPointerDown = event => {
          this.requestedIndex = null;
          this.pointerStart = { id: event.pointerId, left: this.track.scrollLeft, x: event.clientX, y: event.clientY };
        };
        this.onPointerMove = event => {
          if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;
          const x = Math.abs(event.clientX - this.pointerStart.x);
          const y = Math.abs(event.clientY - this.pointerStart.y);
          // Native touch panning cancels the pointer before its first scroll event.
          if (x > 8 && x > y) this.pinSelection();
        };
        this.onPointerUp = event => {
          if (this.pointerStart && this.pointerStart.id === event.pointerId) this.pointerStart = null;
        };
        this.onWheel = event => {
          if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || (event.shiftKey && event.deltaY)) {
            this.pinSelection();
          }
        };
        this.onHoverEnter = this.handleHoverEnter.bind(this);
        this.onHoverLeave = this.handleHoverLeave.bind(this);
        this.onResize = () => this.goTo(this.requestedIndex === null ? this.index : this.requestedIndex, false);
        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeyDown);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.track.addEventListener('pointerdown', this.onPointerDown, { passive: true });
        this.track.addEventListener('pointermove', this.onPointerMove, { passive: true });
        this.track.addEventListener('wheel', this.onWheel, { passive: true });
        window.addEventListener('pointerup', this.onPointerUp, { passive: true });
        window.addEventListener('pointercancel', this.onPointerUp, { passive: true });
        this.stage.addEventListener('pointerenter', this.onHoverEnter);
        this.stage.addEventListener('pointerleave', this.onHoverLeave);
        this.controls.addEventListener('pointerleave', this.onHoverLeave);
        this.addEventListener('pointerleave', this.onHoverLeave);
        if (window.ResizeObserver) {
          this.resizeObserver = new ResizeObserver(this.onResize);
          this.resizeObserver.observe(this.track);
        } else {
          window.addEventListener('resize', this.onResize, { passive: true });
        }
        this.controls.hidden = false;
        this.update(this.closestSlide());
        this.lastAnnouncedIndex = this.index;
      });
    }

    disconnectedCallback() {
      if (!this.ready) return;
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeyDown);
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('wheel', this.onWheel);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      this.stage.removeEventListener('pointerenter', this.onHoverEnter);
      this.stage.removeEventListener('pointerleave', this.onHoverLeave);
      this.controls.removeEventListener('pointerleave', this.onHoverLeave);
      this.removeEventListener('pointerleave', this.onHoverLeave);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      else window.removeEventListener('resize', this.onResize);
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      clearTimeout(this.scrollTimer);
      this.pointerStart = null;
      this.ready = false;
    }

    handleHoverEnter(event) {
      if (event.pointerType !== 'mouse' || this.manualSelection || this.hoverPreview) return;
      if (!window.matchMedia('(any-hover: hover) and (any-pointer: fine)').matches) return;
      // A pointer preview must not hide a photo link being used from the keyboard.
      if (this.track.contains(document.activeElement)) return;
      this.hoverPreview = true;
      this.goTo(1, false);
    }

    handleHoverLeave(event) {
      if (!this.hoverPreview || this.manualSelection) return;
      const next = event.relatedTarget;
      // Keep the preview while moving to its arrows, so Next means photo three.
      if (next instanceof Node && (this.stage.contains(next) || this.controls.contains(next))) return;
      this.hoverPreview = false;
      this.goTo(0, false);
    }

    pinSelection() {
      this.manualSelection = true;
      this.hoverPreview = false;
      this.requestedIndex = null;
    }

    handleClick(event) {
      const button = event.target.closest('[data-vv-sock-index], [data-vv-sock-step]');
      if (!button || !this.controls.contains(button) || button.disabled) return;
      const current = this.requestedIndex === null ? this.index : this.requestedIndex;
      const target = button.hasAttribute('data-vv-sock-index')
        ? Number(button.dataset.vvSockIndex)
        : current + Number(button.dataset.vvSockStep);
      this.pinSelection();
      // Explicit choices take effect immediately, so rapid clicks never wait on
      // an earlier smooth scroll or restore its intermediate control state.
      this.goTo(target, false);
    }

    handleKeyDown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (!this.track.contains(event.target) && !this.controls.contains(event.target)) return;
      const current = this.requestedIndex === null ? this.index : this.requestedIndex;
      const targets = { ArrowLeft: current - 1, ArrowRight: current + 1, Home: 0, End: this.slides.length - 1 };
      if (!(event.key in targets)) return;
      event.preventDefault();
      const wasImageLink = event.target.matches('.vv-sock-card__image-link');
      this.pinSelection();
      const target = this.goTo(targets[event.key], false);
      if (wasImageLink) this.slides[target].querySelector('a').focus({ preventScroll: true });
    }

    goTo(index, animate) {
      const target = Math.max(0, Math.min(index, this.slides.length - 1));
      this.requestedIndex = target;
      this.update(target);
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({
        left: this.slides[target].offsetLeft,
        behavior: animate && !reducedMotion ? 'smooth' : 'instant'
      });
      this.scheduleAnnouncement();
      return target;
    }

    closestSlide() {
      const left = this.track.scrollLeft;
      let closest = 0;
      let distance = Infinity;
      this.slides.forEach((slide, index) => {
        const current = Math.abs(slide.offsetLeft - left);
        if (current < distance) { distance = current; closest = index; }
      });
      return closest;
    }

    handleScroll() {
      if (this.pointerStart && Math.abs(this.track.scrollLeft - this.pointerStart.left) > 3) {
        this.pinSelection();
      }
      if (!this.scrollFrame) {
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.update(this.closestSlide());
        });
      }
      this.scheduleAnnouncement();
    }

    scheduleAnnouncement() {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this.requestedIndex = null;
        this.update(this.closestSlide());
        if (this.status && this.index !== this.lastAnnouncedIndex) {
          this.status.textContent = `Photo ${this.index + 1} of ${this.slides.length}: ${this.slides[this.index].dataset.vvSockLabel}.`;
          this.lastAnnouncedIndex = this.index;
        }
      }, 180);
    }

    update(index) {
      this.index = index;
      this.slides.forEach((slide, slideIndex) => {
        const current = slideIndex === index;
        slide.setAttribute('aria-hidden', String(!current));
        slide.querySelectorAll('a').forEach(link => { link.tabIndex = current ? 0 : -1; });
      });
      this.dots.forEach((dot, dotIndex) => {
        if (dotIndex === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.previous) this.previous.disabled = index === 0;
      if (this.next) this.next.disabled = index === this.slides.length - 1;
    }
  }

  customElements.define('vv-sock-card', VVSockCard);
})();
